import { useState } from "react";
import AuthScreen from "./components/common/AuthScreen";
import ErrorScreen from "./components/common/ErrorScreen";
import LoadingScreen from "./components/common/LoadingScreen";
import OCRResultForm from "./components/common/OCRResultForm";
import ResultScreen from "./components/common/ResultScreen";
import WelcomeScreen from "./components/common/WelcomeScreen";
import NIDCaptureBoth from "./components/NIDCaptureBoth";
import OwnPhotoUpload from "./components/OwnPhotoUpload";
import { ERROR_CODES, ERROR_MESSAGES } from "./config/constants";
import apiClient from "./sdk/apiClient";
import ocrVerification from "./sdk/ocrVerification";
import { validateNidImages } from "./utils/validationUtils";

const App = () => {
  const [currentStep, setCurrentStep] = useState("auth");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [previousStep, setPreviousStep] = useState(null);

  // Data storage
  const [nidImages, setNidImages] = useState({ front: null, back: null });
  const [ocrData, setOcrData] = useState(null);
  const [editableOcrData, setEditableOcrData] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [faceMatch, setFaceMatch] = useState(null);
  const [transactionid, setTransactionid] = useState(null);
  

  const handleNIDCapture = (images) => {
    setNidImages(images);
    setCurrentStep("processing_ocr");
    processOCR(images);
  };

  const processOCR = async (images) => {
    setLoading(true);
    setError(null);

    try {
      // Validate that both images are available
      if (!validateNidImages(images)) {
        throw new Error("Both NID front and back images are required");
      }

      // Process OCR for front image using Tesseract.js (completely offline)
      const frontOCR = await ocrVerification.processNIDFront(images.front);

      // Process OCR for back image using Tesseract.js (completely offline)
      const backOCR = await ocrVerification.processNIDBack(images.back);

      // Combine OCR data
      const combinedOCRData = {
        ...frontOCR.data,
        address: backOCR.data.address,
      };

      setOcrData(combinedOCRData);
      setEditableOcrData(combinedOCRData);

      // Show OCR results before proceeding to liveness detection
      setCurrentStep("ocr_result");
    } catch (error) {
      console.error("OCR Error:", error);
      setError({
        errorCode: error.errorCode || ERROR_CODES.E100,
        message: error.message || ERROR_MESSAGES.E100,
      });
      setCurrentStep("error");
    } finally {
      setLoading(false);
    }
  };

  const resetProcess = () => {
    setCurrentStep("welcome");
    setError(null);
    setResult(null);
    setPreviousStep(null);
    setNidImages({ front: null, back: null });
    setOcrData(null);
    setEditableOcrData(null);
    setSelfieImage(null);
    setFaceMatch(null);
    setTransactionid(null);
  };

  const handleAuthSuccess = (token) => {
    setIsAuthenticated(true);
    setCurrentStep("welcome");
  };

  const handleOCRResultConfirm = async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.callNidFaceVerificationApi(payload);

      // Check if there's an error in the nested result
      if (response?.data?.result?.isError) {
        setError({
          errorCode: response.data.result.statusCode || ERROR_CODES.E500,
          message: response.data.result.errorMsg || "Verification failed. Please try again.",
        });
        setPreviousStep(currentStep);
        setCurrentStep("error");
        return;
      }

      // Success flow
      setOcrData(editableOcrData);
      
      // Store transactionid from API response
      if (response?.data?.data?.transactionId) {
        setTransactionid(response.data.data.transactionId);
      } else if (response?.data?.transactionId) {
        setTransactionid(response.data.transactionId);
      }
      setCurrentStep("liveness_check");
    } catch (err) {
      console.error("OCR Result Confirmation Error:", err);
      setError({
        errorCode: ERROR_CODES.E500,
        message:
          err.message ||
          "An error occurred during verification. Please try again.",
      });
      setPreviousStep(currentStep);
      setCurrentStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handleLivenessCheckConfirm = async (payload) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.callNidFaceVerificationApi(payload);
console.log('response', response);

      // Check if there's an error in the nested result
      if (response?.data?.result?.isError) {
        setError({
          errorCode: response.data.result.statusCode || ERROR_CODES.E500,
          message: response.data.result.errorMsg || "Verification failed. Please try again.",
        });
        setPreviousStep(currentStep);
        setCurrentStep("error");
        return;
      }

      // Success flow - use verification data from response.data.data
      setSelfieImage(payload.selfieImage);
      setFaceMatch(response?.data?.data);
      setResult({
        success: true,
        message: "Verification completed successfully",
        data: {
          ocrData: ocrData,
          selfieImage: payload.selfieImage,
          faceMatch: response?.data?.data,
        },
      });
      setCurrentStep("result");
    } catch (err) {
      console.error("Liveness Check Confirmation Error:", err);
      setError({
        errorCode: ERROR_CODES.E500,
        message:
          err.message ||
          "An error occurred during verification. Please try again.",
      });
      setPreviousStep(currentStep);
      setCurrentStep("error");
    } finally {
      setLoading(false);
    }
  };

  const renderOCRResult = () => {
    const handleFieldChange = (field, value) => {
      setEditableOcrData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    return (
      <OCRResultForm
        ocrData={ocrData}
        editableOcrData={editableOcrData}
        onFieldChange={handleFieldChange}
        onConfirm={handleOCRResultConfirm}
        onStartOver={() => setCurrentStep("welcome")}
      />
    );
  };

  const renderWelcome = () => (
    <WelcomeScreen onStart={() => setCurrentStep("capture_nid")} />
  );

  const renderAuth = () => <AuthScreen onAuthSuccess={handleAuthSuccess} />;

  const renderLoading = () => <LoadingScreen />;

  const handleRetry = () => {
    setError(null);
    if (previousStep) {
      setCurrentStep(previousStep);
      setPreviousStep(null);
    } else {
      resetProcess();
    }
  };

  const renderError = () => (
    <ErrorScreen
      error={error}
      onRetry={handleRetry}
      onHome={resetProcess}
    />
  );

  const renderResult = () => (
    <ResultScreen result={result} onNewVerification={resetProcess} />
  );

  // Render based on current step
  switch (currentStep) {
    case "auth":
      return renderAuth();
    case "welcome":
      return renderWelcome();
    case "capture_nid":
      return (
        <NIDCaptureBoth
          onCapture={handleNIDCapture}
          onBack={() => setCurrentStep("welcome")}
        />
      );
    case "processing_ocr":
      return renderLoading();
    case "ocr_result":
      return renderOCRResult();
    case "liveness_check":
      return (
        <OwnPhotoUpload
          onConfirm={handleLivenessCheckConfirm}
          transactionid={transactionid}
        />
      );
    case "error":
      return renderError();
    case "result":
      return renderResult();
    default:
      return renderWelcome();
  }
};

export default App;
